import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AccessService } from '@app-core/services/access/access.service';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { getClientConfig } from '@config/client-config';
import { internalRoutes } from '@config/config.base';
import { DataService } from '@app-core/services/data/data.service';
import { navigationRoutes } from '@config/links.base';
import { TranslateService } from '@ngx-translate/core';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { StartupService } from '@app-core/services/startup/startup.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  public clientConfig = getClientConfig().configuration;
  constructor(
    private accessService: AccessService,
    private router: Router,
    private dataService: DataService,
    private snackBarService: SnackBarService,
    public translate: TranslateService,
    private durationRangeService: DurationRangeService,
    private startupService: StartupService
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
    const isLoggedIn = this.accessService.hasValidToken();
    const isAllowed = this.clientConfig.allowedRoutes.concat(internalRoutes).includes(route.url[0].path);
    const companion_app_token = localStorage.getItem('companion-app-access-token');

    if (!isLoggedIn) {
      const wildcardRouteTree = this.router.parseUrl(this.clientConfig.wildcardRoute);
      const companion_app_token = localStorage.getItem('companion-app-access-token');
      if (!companion_app_token) {
        this.accessService.clearLocalStorage();
      }
      wildcardRouteTree.queryParams = { redirectUrl: state.url };
      return wildcardRouteTree;
    }

    // Handle empty path routes (route.url may be empty array for routes with path: '')
    const path = route.url.length > 0 ? route.url[0].path : '';
    const currentPathConfig = navigationRoutes[path];
    const planDetails = this.durationRangeService?.planDetails;

    // If route has featureFlag but planDetails not loaded yet, wait for it
    if (currentPathConfig?.hasOwnProperty('featureFlag') && !planDetails) {
      return this.startupService.clientConfig.pipe(
        filter((config) => config && Object.keys(config).length > 0),
        take(1),
        map(() => this.checkAccessAndNavigate(route, state, isAllowed, companion_app_token))
      );
    }

    return this.checkAccessAndNavigate(route, state, isAllowed, companion_app_token);
  }

  private checkAccessAndNavigate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
    isAllowed: boolean,
    companion_app_token: string | null
  ): boolean | UrlTree {
    // Handle empty path routes (route.url may be empty array for routes with path: '')
    const path = route.url.length > 0 ? route.url[0].path : '';
    const isAccessible = path === '' ? true : this.isRouteAccessible(path);
    if (!(isAllowed && isAccessible)) {
      const lang = this.dataService._currentLanguage.value || 'en';
      this.translate
        .getTranslation(lang)
        .pipe(take(1))
        .subscribe((lang) => {
          if (!companion_app_token) {
            this.snackBarService.failure(lang['pageAccessDenied']);
          }
        });
      if (companion_app_token && companion_app_token.length > 0) {
        this.router.navigate(['notifications-page']);
      } else {
        this.router.navigate(['home']);
      }
      return false;
    }
    return true;
  }

  public isRouteAccessible(path: string) {
    const userInfo = this.accessService.getLoginInfo();
    const currentFleetId = this.dataService._currentFleet.value;
    const currentFleetInfo = (userInfo?.fleets || []).find((f) => f.fleetId === currentFleetId);

    const isInternalRoute = internalRoutes.includes(path);
    const currentPathConfig = navigationRoutes[path];

    const planDetails = this.durationRangeService?.planDetails;

    if (!currentPathConfig || !currentPathConfig.hasOwnProperty('featureFlag') || !planDetails) {
      const isAccessible =
        isInternalRoute ||
        !currentPathConfig?.uiConfigKey ||
        currentFleetInfo?.role?.uiPermissions?.includes(currentPathConfig.uiConfigKey);
      return isAccessible;
    }

    const currentFeatureFlag = currentPathConfig.featureFlag;
    const featureFlags = planDetails?.featureFlags;
    const hasFeatureFlagAccess = currentFeatureFlag && featureFlags.includes(currentFeatureFlag);

    const isAccessible =
      isInternalRoute ||
      !currentPathConfig.uiConfigKey ||
      (currentFleetInfo?.role?.uiPermissions?.includes(currentPathConfig.uiConfigKey) && hasFeatureFlagAccess);

    return isAccessible;
  }
}
